import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import {
	createSummaryTo,
	SummaryCardSection,
	SummaryField,
	SummaryFieldGroup,
} from "@portal/components/multiform";
import { useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { formatEmpty } from "../../../../../../utils";
import { AdminBondRequestContext } from "../../AdminBondRequestContext";
import { type AdminBondRequestFormData, fp } from "../../schema";

export const PrincipalSummary = () => {
	const { getValues, watch } = useFormContext<AdminBondRequestFormData>();
	const { pathname } = useLocation();
	const { adminUsers } = useAdminFetchAdminUsers();
	const { agencyPreviews } = useAdminFetchAgencyPreviews();
	const { selectedAccount } = useContext(AdminBondRequestContext);
	const agencyId = watch("agencyId");
	const { agentPreviews } = useAdminFetchAgentPreviewsForAgency(agencyId, true);
	const formValues = getValues();

	const to = createSummaryTo(pathname, "principal");

	const selectedAgency = agencyPreviews?.find((agency) => agency.id === formValues.agencyId);
	const selectedAgent = agentPreviews?.find((agent) => agent.id === formValues.agentId);

	const principalName = selectedAccount?.companies.find(
		(c) => c.id === formValues.quote?.principal?.id,
	)?.name;

	const underwriterName = useMemo(() => {
		const underwriter = adminUsers?.find((user) => user.id === formValues.underwriterId);
		return formatEmpty(
			underwriter ? `${underwriter.firstName} ${underwriter.lastName}` : undefined,
		);
	}, [adminUsers, formValues.underwriterId]);

	const followerNames = useMemo(() => {
		if (!formValues.followers?.length || !agentPreviews) return [];
		return formValues.followers.map((id) => {
			const agent = agentPreviews.find((a) => a.id === id);
			return agent ? agent.displayName : "";
		});
	}, [agentPreviews, formValues.followers]);

	if (!adminUsers || !agencyPreviews) return null;

	return (
		<SummaryCardSection header="Principal" to={{ pathname, section: "principal" }}>
			<SummaryFieldGroup>
				<SummaryField
					label="Principal"
					value={formatEmpty(principalName)}
					fieldPath={fp("suretyAccountId")}
					to={to("suretyAccountId")}
				/>
				<SummaryField
					label="Agency"
					value={formatEmpty(selectedAgency?.displayName)}
					fieldPath={fp("agencyId")}
					to={to("agencyId")}
				/>
				<SummaryField
					label="Agent"
					value={formatEmpty(selectedAgent?.displayName)}
					fieldPath={fp("agentId")}
					to={to("agentId")}
				/>
				<SummaryField
					label="Underwriter"
					value={underwriterName}
					fieldPath={fp("underwriterId")}
					to={to("underwriterId")}
				/>
				<SummaryField
					label="Followers"
					value={followerNames}
					fieldPath={fp("followers")}
					to={to("followers")}
					vertical
				/>
			</SummaryFieldGroup>
		</SummaryCardSection>
	);
};
